import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import makeAsyncScriptLoader from 'react-async-script';

import { useSoyuzClientConfig, useSoyuzLocales } from '@spotahome/soyuz/client';

import { useThemeContext } from '../..';

const callbackName = 'onloadcallback';
const globalName = 'grecaptcha';

const noOp = () => {};

const MAX_RETRIES = 5;

const ReCaptcha = ({ onChange, onExpired, onError = noOp, className = '' }) => {
  const containerRef = useRef(null);
  const recaptchaIdRef = useRef(null);
  const executeRequestedRef = useRef(false);
  const retriesRef = useRef(0);
  const { theme } = useThemeContext();
  const { current: currentLocale } = useSoyuzLocales();
  const { recaptchaSiteKeys } = useSoyuzClientConfig();

  const getCaptchaFunction = fnName =>
    window.grecaptcha?.enterprise?.[fnName] || null;

  // eslint-disable-next-line consistent-return
  const executeCaptcha = () => {
    const execute = getCaptchaFunction('execute');
    if (execute && recaptchaIdRef.current !== undefined) {
      return execute(recaptchaIdRef.current);
    }
    executeRequestedRef.current = true;
  };

  useEffect(() => {
    let interval = null;
    const initializeCaptcha = () => {
      if (retriesRef.current >= MAX_RETRIES) {
        clearInterval(interval);
        return;
      }

      const render = getCaptchaFunction('render');
      if (render && !recaptchaIdRef.current) {
        const wrapper = document.createElement('div');
        recaptchaIdRef.current = render(wrapper, {
          sitekey: recaptchaSiteKeys[theme] || recaptchaSiteKeys.spotahome,
          callback: onChange,
          theme: 'light',
          type: 'image',
          tabindex: 0,
          'expired-callback': onExpired,
          'error-callback': onError,
          size: 'normal',
          // stoken: this.props.stoken,
          hl: currentLocale,
          badge: 'bottomright',
          isolated: false
        });
        containerRef.current.appendChild(wrapper);
        clearInterval(interval);
      }
      if (executeRequestedRef.current && recaptchaIdRef.current) {
        executeRequestedRef.current = false;
        executeCaptcha();
      }
      retriesRef.current += 1;
    };

    if (!recaptchaIdRef.current) {
      interval = setInterval(initializeCaptcha, 200);
    }

    return () => clearInterval(interval);
  }, []);

  return <div ref={containerRef} className={className} />;
};

ReCaptcha.propTypes = {
  onChange: PropTypes.func.isRequired,
  onExpired: PropTypes.func.isRequired,
  onError: PropTypes.func,
  className: PropTypes.string
};

function getOptions() {
  return (typeof window !== 'undefined' && window.recaptchaOptions) || {};
}

function getURL() {
  const hostname = 'www.google.com';
  return `https://${hostname}/recaptcha/api.js?onload=${callbackName}&render=explicit`;
}

export default makeAsyncScriptLoader(getURL, {
  callbackName,
  globalName,
  attributes: getOptions().nonce ? { nonce: getOptions().nonce } : {}
})(ReCaptcha);
